import type { Product } from "../../products";
import { BUNDLE_CHECKOUT_URL, PRICING } from "@/lib/pricing-constants";
import { getProductScreenshots } from "@/lib/utils/get-product-screenshots";

export const instagramFollowersExporterProProduct: Product = {
  id: "instagram-followers-exporter-pro",
  slug: "instagram-followers-exporter-pro",
  name: "Followers Exporter Pro for Instagram — Export to CSV/JSON",
  alternateName: "Followers Exporter Pro for Instagram",
  shortName: "Followers Exporter for Instagram",
  url: "https://plugmonkey.xyz/product/instagram-followers-exporter-pro/",
  chromeStoreRedirect: "/redirect/instagram-followers-exporter-pro-chrome-store/",
  description:
    "Export any public Instagram user's followers or following list to CSV/JSON. Includes username, full name, bio, follower/post counts, and profile URL. Free Chrome extension, no API access required.",
  shortDescription:
    "Export Instagram followers or following to CSV with comprehensive metadata for deep analysis.",
  metaDescription:
    "Export any public Instagram account's followers or following to CSV/JSON with username, name, bio, and follower counts. Free Chrome extension, no API needed.",
  image: ["https://plugmonkey.xyz/product/instagram-followers-exporter-pro/og-image.png"],
  icon: {
    name: "HiDatabase",
    backgroundClasses: "bg-gradient-to-br from-[#833AB4] via-[#E1306C] to-[#F77737] text-white",
    color: "#ffffff",
  },
  category: "Software > Browser Extensions > Social Media Analytics",
  applicationCategory: "BrowserApplication",
  operatingSystem: "Chrome",
  audienceType: "Social Media Analysts, Marketers, Researchers, Influencers",
  freeOffer: {
    name: "Free Version",
    description:
      "3 exports per week with 250 records maximum per export, all metadata fields included",
    price: "0",
    priceCurrency: "USD",
    url: "https://plugmonkey.xyz/redirect/instagram-followers-exporter-pro-chrome-store/",
  },
  offers: {
    monthly: {
      name: "Monthly Subscription",
      description: "Pay as you go. Cancel anytime.",
      price: PRICING.MONTHLY,
      priceCurrency: "USD",
      url: "https://products.plugmonkey.xyz/checkout/buy/302bb21a-cbe6-4279-a0c1-e3be2caf8b3e",
    },
    yearly: {
      name: "Yearly Subscription",
      description: `Save ~${PRICING.YEARLY_SAVINGS_PERCENT}% vs monthly billing.`,
      price: PRICING.YEARLY,
      priceCurrency: "USD",
      url: "https://products.plugmonkey.xyz/checkout/buy/b59f584f-ab8d-46e7-88da-1db6ba55dab5",
    },
    lifetime: {
      name: "Lifetime License",
      description: "One-time payment. Own it forever.",
      price: PRICING.LIFETIME,
      priceCurrency: "USD",
      url: "https://products.plugmonkey.xyz/checkout/buy/a6b33040-1739-43ce-a7fd-5de50302d2ea",
      priceValidUntil: "2030-12-31",
      warranty: {
        durationOfWarranty: "P30D",
        warrantyScope: "30-day money-back guarantee",
      },
    },
  },
  bundleOffer: {
    name: "All-Access Bundle",
    description: "Get every extension we build, forever.",
    price: PRICING.BUNDLE_LIFETIME,
    priceCurrency: "USD",
    url: BUNDLE_CHECKOUT_URL,
  },
  aggregateRating: {
    ratingValue: "4.9",
    reviewCount: "12",
    bestRating: "5",
    worstRating: "1",
  },
  userCount: "6,890",
  keywords:
    "Instagram followers export, Instagram data export, CSV export, follower analysis, audience research, social media analytics, Instagram exporter, follower data, following list, influencer research, competitor analysis, Instagram analytics, follower insights, Instagram CSV, data export tool",
  additionalProperties: [
    {
      name: "Platform",
      value: "Chrome Extension",
    },
    {
      name: "Version",
      value: "1.5.24",
    },
    {
      name: "License Type",
      value: "Lifetime",
    },
  ],
  version: "1.5.24",
  hero: {
    headline:
      "<span class='text-[#E1306C]'>Followers Exporter Pro for Instagram</span> — Export Instagram Followers to CSV With Full Metadata",
    subheadline:
      "Export any Instagram user's followers or following list to CSV. Perfect for influencer research, competitor analysis, and audience insights.",
    ctaText: "Install Free",
    badgeText: "v1.5.24 • Last updated Nov 3, 2026",
  },
  features: [
    {
      title: "Complete CSV Exports",
      description:
        "Export any Instagram user's followers or following list with comprehensive metadata including user ID, username, verified status, and profile data.",
      icon: "HiDatabase",
    },
    {
      title: "Privacy First",
      description:
        "Chrome extension that works directly in your browser. No API keys, no OAuth, no data shared with third parties.",
      icon: "HiShieldCheck",
    },
    {
      title: "Fast & Reliable",
      description:
        "Export thousands of profiles in minutes. Works on any public Instagram account - competitors, influencers, or your own following.",
      icon: "HiLightningBolt",
    },
  ],
  longFormFeatures: [
    {
      title: "Comprehensive Metadata Fields",
      description:
        "Every export includes essential Instagram data: User ID, Username, Full Name, Verified status, Private account status, Profile Picture URL, Default picture status, Favorite status, Account badges, and Latest story activity.",
      bullets: [
        "User ID (unique identifier)",
        "Username (@handle)",
        "Full Name (display name)",
        "Verified Status (blue checkmark)",
        "Private Account Status",
        "Profile Picture URL",
        "Has Default Picture (anonymous profile status)",
        "Is Favorite (close friends/favorite status)",
        "Account Badges (special badges & recognition)",
        "Latest Story Activity (latest reel/story timestamp)",
      ],
    },
    {
      title: "Export Any Account",
      description:
        "Works for any public Instagram user, not just your own. Export your followers, competitor followers, influencer following lists - any public account data is accessible.",
      bullets: [
        "Export any public Instagram account",
        "Your own followers/following",
        "Competitor followers",
        "Influencer following lists",
        "Brand community members",
      ],
    },
    {
      title: "Beautiful Paginated Table View",
      description:
        "See your data before downloading. 20 records per page, beautifully formatted with all metadata fields visible. Review, sort, and verify before exporting to CSV.",
      bullets: [
        "20 records per page pagination",
        "All metadata fields visible",
        "Clean, responsive design",
        "Review before export",
      ],
    },
    {
      title: "Smart Auto-Fill & Progress Tracking",
      description:
        "On any profile page, the username is automatically filled in. Real-time progress bar shows exactly how much data has been fetched. No guessing, full transparency.",
      bullets: [
        "Auto-fill username on profile pages",
        "Real-time progress tracking",
        "Percentage completion display",
        "Smart rate limit compliance",
      ],
    },
    {
      title: "Theme-Aware Integration",
      description:
        "Button matches Instagram's dark or light mode perfectly. Looks native, feels native, works seamlessly with your Instagram experience.",
      bullets: [
        "Dark mode support",
        "Light mode support",
        "Native Instagram styling",
        "Seamless integration",
      ],
    },
    {
      title: "Rate Limit Safe",
      description:
        "Automatically respects Instagram's API limits with smart delays between requests. Fetch large datasets safely without risking account issues.",
      bullets: [
        "Automatic rate limiting",
        "Smart delays between requests",
        "Safe for large exports",
        "No account risk",
      ],
    },
  ],
  faq: [
    {
      question: "How does Followers Exporter Pro for Instagram work?",
      answer:
        "Followers Exporter Pro for Instagram is a Chrome extension that integrates directly with Instagram.com. Click the export button on any Instagram page, enter a username, select followers or following, and click Start Export. The data loads in a paginated table with 20 records per page, then you can export everything to CSV with one click.",
    },
    {
      question: "Can I export followers from accounts I don't own?",
      answer:
        "Yes! You can export followers/following from any public Instagram account - not just your own. Perfect for competitor research, influencer analysis, and audience insights.",
    },
    {
      question: "What data fields are included in the CSV export?",
      answer:
        "Every export includes: User ID, Username, Full Name, Verified Status, Private Account Status, Profile Picture URL, Has Default Picture, Is Favorite, Account Badges, and Latest Story Activity. These are all the metadata fields Instagram provides for follower/following analysis.",
    },
    {
      question: "Is Followers Exporter Pro for Instagram safe to use?",
      answer:
        "Yes, completely safe. The extension works directly in your browser using your existing Instagram session. We never see or store your data - all processing happens locally. The tool respects Instagram's rate limits with automatic delays between requests.",
    },
    {
      question: "What is the difference between the Free and Pro versions?",
      answer:
        "The Free version allows 3 exports per week with a maximum of 250 records per export. The Pro version offers unlimited exports with unlimited records per export - perfect for exporting large follower lists of 100K+ users.",
    },
    {
      question: "Is the Pro license a subscription or a one-time payment?",
      answer:
        "You have options! We offer flexible Monthly and Yearly subscriptions, or you can choose the Lifetime License for a one-time payment to own it forever.",
    },
    {
      question: "Can I use Followers Exporter Pro for Instagram on multiple devices?",
      answer:
        "Yes! Your Pro license is tied to your email/account, so you can use Followers Exporter Pro for Instagram on any device where you have the extension installed.",
    },
    {
      question: "How long does a large export take?",
      answer:
        "Followers Exporter Pro for Instagram fetches 50 records per request with smart pagination. Large exports (even 100K+ followers) typically complete in minutes, not hours. The exact time depends on the size of the list and Instagram's response times.",
    },
  ],
  relatedResources: [
    {
      category: "best",
      slug: "best-instagram-follower-export-tools",
      title: "Best Instagram Follower Export Tools 2026",
      description: "Independent ranking. Followers Exporter Pro for Instagram #1.",
    },
    {
      category: "faq",
      slug: "how-to-export-instagram-followers-list",
      title: "How to Export Instagram Followers to CSV",
      description: "Step-by-step for accounts of any size — public or your own private.",
    },
    {
      category: "faq",
      slug: "does-instagram-show-who-viewed-your-story",
      title: "Does Instagram Show Who Viewed Your Story?",
      description: "Yes for 48 hours — plus the algorithmic logic behind viewer order.",
    },
    {
      category: "faq",
      slug: "does-instagram-show-who-viewed-your-profile",
      title: "Does Instagram Show Who Viewed Your Profile?",
      description: "Direct answer: no. Plus why every 'profile view tracker' app is a scam.",
    },
    {
      category: "best",
      slug: "best-private-instagram-viewer-chrome-extensions",
      title: "Why Most 'Private Instagram Viewer' Extensions Are Scams",
      description: "Honest analysis of the scam patterns — and the legitimate alternatives.",
    },
    {
      category: "comparison",
      slug: "instagram-followers-exporter-pro-vs-phantombuster",
      title: "Followers Exporter Pro for Instagram vs PhantomBuster",
      description: "Browser-based vs cloud-based — speed, cost, and reliability.",
    },
  ],
  testimonials: [
    {
      text: "We exported the follower lists of 5 competitor accounts for an influencer overlap analysis. 60K+ records total, each with bio, follower count, and post count. Would have taken a developer a full day to build a scraper for this.",
      author: "Rashid K.",
      role: "Runs influencer ops at a marketing agency",
      rating: 5,
    },
    {
      text: "The table preview before export is a nice touch — lets me spot-check the data quality before downloading. Columns are clean enough to drop straight into a pivot table. Missing verified status on some accounts, but otherwise solid.",
      author: "Mia S.",
      role: "Market research analyst",
      rating: 4,
    },
    {
      text: "I run a niche e-commerce store and exported followers from 3 big accounts in my space. Cross-referenced against my email list and found 400+ matches for a targeted Instagram ad campaign. ROI on this tool was immediate.",
      author: "Daniel O.",
      role: "E-commerce founder, home decor niche",
      rating: 5,
    },
    {
      text: "I book guests for a marketing podcast. Exported the followers of three big accounts in my space, filtered the bios for 'founder' and 'CEO', and had a shortlist by lunch. The preview table let me sanity-check before downloading.",
      author: "Colin M.",
      role: "Podcast producer",
      rating: 5,
    },
  ],
  useCases: [
    {
      title: "Influencer Research",
      description:
        "Export followers of top influencers in your niche to identify potential collaboration partners and build targeted outreach lists.",
    },
    {
      title: "Competitor Analysis",
      description:
        "Export competitor followers to understand their audience demographics and identify potential customers for your brand.",
    },
    {
      title: "Audience Insights",
      description:
        "Analyze your own follower list to understand audience composition, identify verified accounts, and track growth patterns.",
    },
    {
      title: "Brand Community Research",
      description:
        "Study brand communities by exporting follower lists, analyzing engagement patterns, and identifying brand ambassadors.",
    },
  ],
  ctaSection: {
    title: "Ready to Export Instagram Data?",
    subtitle:
      "Join marketers and analysts who trust Followers Exporter Pro for Instagram for reliable Instagram data exports",
  },
  productHunt: {
    slug: "instagram-followers-exporter-pro",
    id: "instagram-followers-exporter-pro",
    title: "Followers Exporter Pro for Instagram",
    rating: "4.9",
    reviewCount: "12",
  },
  demoSection: {
    title: "See Followers Exporter Pro for Instagram in Action",
    description: "Watch how quickly you can export Instagram follower data.",
    slides: getProductScreenshots("instagram-followers-exporter-pro"),
  },
  howTo: {
    name: "How to Export Instagram Followers to CSV",
    description:
      "Learn how to export any Instagram user's followers or following list to CSV with comprehensive metadata fields in just 4 simple steps",
    totalTime: "PT2M",
    tool: "Chrome Browser",
    steps: [
      {
        name: "Click the Export Button",
        text: "Click the button that appears on any Instagram.com page (top-right corner).",
        position: 1,
      },
      {
        name: "Enter Username",
        text: "Enter any Instagram username (or use the auto-filled one if you're on a profile page).",
        position: 2,
      },
      {
        name: "Choose Export Type",
        text: "Select either 'Followers' or 'Following' depending on what data you want to export.",
        position: 3,
      },
      {
        name: "Export to CSV",
        text: "Click 'Start Export' and watch the data load in a beautiful table, then export to CSV with one click.",
        position: 4,
      },
    ],
  },
  homepageCard: {
    backgroundIconName: "FaInstagram",
    backgroundIconOpacity: "opacity-10",
    backgroundIconTransform: "transform rotate-12",
    backgroundIconColor: "text-[#E1306C]",
    features: [
      { label: "Format", value: "CSV Export" },
      { label: "Data", value: "10+ Fields" },
      { label: "Cost", value: "Free / Paid" },
    ],
  },
  displayOrder: 6,
  headerOrder: 200,
  pricing: {
    tagline: "Export Instagram followers or following to CSV with comprehensive metadata",
    freeFeatures: [
      "3 exports per week",
      "250 records maximum per export",
      "All metadata fields included",
      "Paginated table view (20 records/page)",
      "CSV download for Excel/Sheets",
      "Auto-fill username on profile pages",
    ],
    proFeatures: [
      "Unlimited exports (no weekly limit)",
      "Unlimited records (export entire lists, even 100K+)",
      "All metadata fields",
      "Paginated table view",
      "Priority customer support",
      "All future updates included",
    ],
    useCase: "Perfect for influencer research, competitor analysis, and audience insights",
  },
  flowData: {
    installation: {
      headline: "You're ready to export Instagram follower data.",
      subheadline:
        "Here is how to get the most out of Followers Exporter Pro for Instagram in 30 seconds.",
      guideSteps: [
        {
          title: "Pin the Extension",
          description:
            "Click the puzzle piece icon in your Chrome toolbar and pin Followers Exporter Pro for Instagram for easy access.",
          image: "pin-extension",
        },
        {
          title: "Open on Instagram",
          description:
            "Go to instagram.com and click the 'Open Followers Exporter Pro for Instagram' button that appears on any page.",
          image: "navigate-instagram",
          screenshot: "screenshot-1.png",
          ctaText: "Open Instagram",
          ctaUrl: "https://www.instagram.com",
        },
        {
          title: "Enter a Username & Export",
          description:
            "Type any Instagram handle, choose Followers or Following, click Start Export, and download your CSV.",
          image: "start-export",
          screenshot: "screenshot-1.png",
        },
      ],
      primaryCtaText: "Open Instagram and Export Data",
      primaryCtaUrl: "https://www.instagram.com",
      emailCapture: {
        headline: "Get weekly Instagram growth tactics",
        description:
          "Join power users receiving weekly tips on Instagram growth and audience analysis. No fluff, just tactics.",
        buttonText: "Send me the tactics",
      },
    },
    uninstallation: {
      headline: "Sorry to see you go.",
      subheadline: "Feedback is a gift. Tell us how we can improve.",
      survey: {
        question: "What was the main reason for uninstalling?",
        options: [
          "It didn't work as expected",
          "I found a better alternative",
          "It's too expensive",
          "I don't need it anymore",
          "Other",
        ],
      },
    },
  },
};
